import { createContext, ReactNode, useContext, useEffect, useRef, useState, useCallback } from 'react';
import envConfig from '@/config/env';
import { WsSubscribeEvent } from '@/workers/ws.worker';
import { useBoomWallet } from '@boomeme/wallet-sdk';

type WsResponseEvent = 'klineUpdate' | 'transactionUpdate' | 'pumpNewborn' | 'pnlUpdate' | 'alert' | 'signalTokenUpdate';

interface WebSocketData {
  event: WsResponseEvent;
  data: any;
}

interface WebSocketContextType {
  data: WebSocketData | null;
  isConnected: boolean;
  connect: () => void;
  disconnect: () => void;
  sendMessage: (event: WsSubscribeEvent, data?: any) => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket必须在WebSocketProvider中使用');
  }
  return context;
};

export const WebSocketProvider = ({ children }: { children: ReactNode }) => {
  const [data, setData] = useState<WebSocketData | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { chain } = useBoomWallet();
  const url = chain == 'bsc' ? envConfig.VITE_CLIENT_WS_URL + '/bsc' : envConfig.VITE_CLIENT_WS_URL + '/sol';

  // 使用useRef存储worker实例
  const workerRef = useRef<Worker | null>(null);
  // 心跳定时器引用
  const heartbeatTimerRef = useRef<NodeJS.Timeout | null>(null);

  // 清理函数
  const cleanup = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // 初始化worker
  const initWorker = useCallback(() => {
    if (workerRef.current) return;

    try {
      // 创建WebSocket Worker
      workerRef.current = new Worker(new URL('../workers/ws.worker.ts', import.meta.url), { type: 'module' });

      // 监听来自worker的消息
      workerRef.current.onmessage = (event) => {
        const { type, payload } = event.data;

        switch (type) {
          case 'data':
            setData(payload);
            break;
          case 'connectionStatus':
            setIsConnected(payload.isConnected);
            break;
          case 'error':
            console.error('WebSocket worker error:', payload.message);
            break;
          default:
            console.warn('未知的worker消息类型', type);
        }
      };

      // 监听worker错误
      workerRef.current.onerror = (error) => {
        console.error('WebSocket worker error:', error);
        cleanup();
      };
    } catch (err) {
      console.error('WebSocket worker initialization failed', err);
    }
  }, [cleanup]);

  // 连接WebSocket
  const connect = useCallback(() => {
    if (!workerRef.current) {
      initWorker();
    }

    if (workerRef.current) {
      workerRef.current.postMessage({
        type: 'connect',
        payload: { url },
      });
    }
  }, [initWorker, url]);

  // 断开WebSocket连接
  const disconnect = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.postMessage({ type: 'disconnect' });
    }
  }, []);

  // 发送消息
  const sendMessage = useCallback((event: WsSubscribeEvent, payload: any) => {
    if (workerRef.current) {
      workerRef.current.postMessage({
        type: 'sendMessage',
        payload: { event, data: payload },
      });
    }
  }, []);

  // 发送心跳消息
  const sendHeartbeat = useCallback(() => {
    if (isConnected && workerRef.current) {
      workerRef.current.postMessage({
        type: 'sendMessage',
        payload: { event: 'ping', data: { timestamp: Date.now() } },
      });
    }
  }, [isConnected]);

  // 启动心跳机制
  const startHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
    }
    heartbeatTimerRef.current = setInterval(sendHeartbeat, 30000);
  }, [sendHeartbeat]);

  useEffect(() => {
    if (isConnected) {
      startHeartbeat();
    } else if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, [isConnected, startHeartbeat]);

  // 组件挂载时初始化WebSocket
  useEffect(() => {
    try {
      // 先清理旧的连接
      cleanup();
      // 重新初始化
      initWorker();
      const initTimeout = setTimeout(connect, 2000);

      return () => {
        clearTimeout(initTimeout);
        cleanup();
      };
    } catch (err) {
      console.error('WebSocket initialization failed', err);
    }
  }, [connect, cleanup, initWorker, chain]);

  return <WebSocketContext.Provider value={{ data, isConnected, connect, disconnect, sendMessage }}>{children}</WebSocketContext.Provider>;
};
